/**
 * The step-up, in the browser (IP-47, IP-76, IP-96; docs/auth/console-identity-and-access.md
 * section 8): an action the server marks stepUp - a file's upload, accept and
 * reject, and a fund's fee change and its approval or rejection (IP-101) -
 * needs a fresh authenticator code, bound to exactly what is being done (the
 * file's fingerprint; the batch and the bytes the checker was shown; the fees,
 * or the proposal and the proposer's digest of them). step-up/start refuses
 * up front what the action would refuse - the uploader accepting their own
 * file, an accept its downstream step would refuse, the proposer deciding
 * their own fee change - so no code is asked for an action that cannot
 * succeed.
 *
 *   begin(actionType, params, submit)
 *     1. POST step-up/start {actionType, params} -> a challenge, with the
 *        SERVER's summary of what the action will do, valid for 120 s;
 *     2. the dialog shows that summary and asks for the code;
 *     3. submit({ challengeId, code }) sends the action itself with stepUp;
 *     4. resolves with the action's answer.
 *
 *   A wrong code (STEP_UP_CODE_INVALID) or a spent or expired challenge
 *   (STEP_UP_CHALLENGE_INVALID) starts a fresh challenge in the same dialog -
 *   each challenge buys exactly one guess. A right code already used for a
 *   step (STEP_UP_CODE_REUSED, 409; IP-111) - two decisions inside one
 *   30-second window - is no mistake and counts no failure: it starts a fresh
 *   challenge too, and says to wait for the app's next code rather than that
 *   the code is wrong. STEP_UP_LOCKED (five wrong codes in
 *   ten minutes) says until when, in Colombo time like the rest of the
 *   Console (details.lockedUntil), and the person can only close the dialog.
 *   cancel() rejects with StepUpCancelled and nothing is sent: the challenge
 *   simply expires, and the rest of the Console carries on.
 *
 * Pure (no React), so it is tested without a browser; useStepUp.js renders it.
 */
import { formatInstant } from '../format.js';

/** STEP_UP_LOCKED in the Console's time: the server's lockedUntil is an instant (UTC); its words are kept when it gives none. */
export function lockedMessage(error) {
  const until = error?.details?.lockedUntil;
  if (!until || Number.isNaN(Date.parse(until))) return error?.message ?? 'Too many wrong codes.';
  return 'Too many wrong codes. Step-up is locked until ' + formatInstant(until) + ' (Colombo time).';
}

export class StepUpCancelled extends Error {
  constructor() {
    super('The action was cancelled.');
    this.name = 'StepUpCancelled';
  }
}

/** The refusals after which the same dialog asks again, on a fresh challenge, and what it says. */
const RESTART_MESSAGES = Object.freeze({
  STEP_UP_CODE_INVALID: 'That code is not right. Enter the code your app shows now.',
  STEP_UP_CHALLENGE_INVALID: 'That code could not be used. Enter the code your app shows now.',
  // IP-111: the code was right but already spent on a step this 30-second window.
  STEP_UP_CODE_REUSED: 'Wait for your app to show a new code, then enter it.',
});
const IDLE = Object.freeze({ open: false, busy: false, error: null, locked: false, summary: null, actionType: null, actionClass: null, expiresAt: null });

/**
 * @param {{ start: (actionType: string, params: object) => Promise<{ challengeId, actionClass, summary, expiresAt, params }> }} ports
 */
export function createStepUpController({ start }) {
  let state = IDLE;
  let pending = null; // { actionType, params, submit, challengeId, resolve, reject }
  const listeners = new Set();

  const set = (patch) => {
    state = { ...state, ...patch };
    for (const listener of listeners) listener(state);
  };

  async function challenge(error = null) {
    set({ busy: true, error });
    const answer = await start(pending.actionType, pending.params);
    pending.challengeId = answer.challengeId;
    set({
      open: true, busy: false, error, locked: false,
      summary: answer.summary, actionType: pending.actionType, actionClass: answer.actionClass, expiresAt: answer.expiresAt,
    });
  }

  function finish(outcome, value) {
    const done = pending;
    pending = null;
    state = IDLE;
    for (const listener of listeners) listener(state);
    if (done) (outcome === 'resolve' ? done.resolve : done.reject)(value);
  }

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    state: () => state,

    /** @returns {Promise<*>} the action's answer; StepUpCancelled if the person closes the dialog */
    begin(actionType, params, submit) {
      if (pending) return Promise.reject(new Error('another step-up is in progress'));
      return new Promise((resolve, reject) => {
        pending = { actionType, params, submit, challengeId: null, resolve, reject };
        set({ ...IDLE, open: true, busy: true, actionType });
        challenge().catch((error) => finish('reject', error));
      });
    },

    /** The six digits the person typed. */
    async enter(code) {
      if (!pending || state.busy || state.locked || !/^\d{6}$/.test(code ?? '')) return;
      set({ busy: true, error: null });
      try {
        const answer = await pending.submit({ challengeId: pending.challengeId, code });
        finish('resolve', answer);
      } catch (error) {
        if (error?.code === 'STEP_UP_LOCKED') {
          set({ busy: false, locked: true, error: lockedMessage(error) });
        } else if (Object.hasOwn(RESTART_MESSAGES, error?.code ?? '')) {
          try {
            await challenge(RESTART_MESSAGES[error.code]);
          } catch (again) {
            finish('reject', again);
          }
        } else {
          finish('reject', error);
        }
      }
    },

    /** A fresh challenge, when the 120 seconds ran out while the person was reading. */
    async renew() {
      if (!pending || state.busy || state.locked) return;
      try {
        await challenge(null);
      } catch (error) {
        finish('reject', error);
      }
    },

    cancel() {
      if (pending) finish('reject', new StepUpCancelled());
    },
  };
}
